import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import axios, { AxiosRequestConfig } from 'axios';
import { useRouter } from 'next/router';
import { enqueueSnackbar } from 'notistack';

import { Auth0ContextInterface, GetTokenSilentlyOptions, useAuth0, User } from '@auth0/auth0-react';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { PUBLIC_API_URL } from '../constants/config';
import { ensureValidToken, useLocalAuth } from '../context/localAuthContext';

type Options = Omit<AxiosRequestConfig, 'url'> & {
  catchCb?: (a: any) => any;
  searchParams?: any;
  url: string;
};

type ExtraOptions<T> = {
  auth?: Auth0ContextInterface<User>;
  setData?: Dispatch<SetStateAction<T>>;
  showErrors?: boolean;
  throwOnError?: boolean;
  useLocalToken?: boolean;
};

interface SnackbarPropsExtended extends React.HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

const API_PREFIX = '/api/v1';

export const requestAccessToken = async (
  auth: Auth0ContextInterface<User>,
  options?: GetTokenSilentlyOptions,
) => {
  try {
    return await auth?.getAccessTokenSilently(options);
  } catch (_) {
    try {
      return await auth?.getAccessTokenWithPopup(options);
    } catch (_) {
      return false;
    }
  }
};

function encodeParams(params: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;

    if (value === null) {
      result[key] = 'null';
      continue;
    }

    if (typeof value === 'bigint') {
      result[key] = value.toString();
      continue;
    }

    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
      result[key] = JSON.stringify(value, (_k, v) => (typeof v === 'bigint' ? v.toString() : v));
      continue;
    }

    result[key] = String(value);
  }

  return result;
}

export async function fetchAPI<T>(options: Options, extra?: ExtraOptions<T>): Promise<T> {
  const { catchCb, data, method = 'GET' } = options;

  let authorization: string | undefined;
  if (extra?.useLocalToken) {
    const validToken = await ensureValidToken();
    if (validToken) {
      authorization = `Bearer ${validToken}`;
    }
  } else if (extra?.auth) {
    const resolved = await requestAccessToken(extra.auth);
    if (resolved) {
      authorization = `Bearer ${resolved}`;
    }
  }

  const headers = {
    ...options.headers,
    ...(authorization && { authorization }),
  };

  const rawParams = { ...options.params, ...options.searchParams };
  const encodedParams = Object.keys(rawParams).length > 0 ? encodeParams(rawParams) : undefined;

  const config = {
    ...options,
    data,
    headers,
    method,
    params: encodedParams,
  };

  try {
    const response = await axios(config);
    if (typeof extra?.setData === 'function') {
      extra.setData(response.data);
    }
    return response.data as T;
  } catch (err: any) {
    if (extra?.throwOnError) {
      throw err;
    }
    if (catchCb) catchCb(err.response?.data);
    if (extra?.showErrors) {
      enqueueSnackbar(err.response?.statusText || err.response?.data?.message?.substring(0, 300), {
        autoHideDuration: 3000,
        variant: 'error',
      });
    }
    return undefined as T;
  }
}

export function useFetch<T>(options?: Options, extra?: ExtraOptions<T>) {
  const [data, setData] = useState<T>();

  useEffect(() => {
    if (options) {
      fetchAPI<T>(options, { ...extra, setData });
    }
  }, [extra, options]);

  return { data } as { data: null | T };
}

function deriveKey(url: string, params?: any, searchParams?: any): unknown[] {
  const key: unknown[] = url.replace(/^\//, '').split('/').filter(Boolean);
  if (params !== undefined) key.push(params);
  if (searchParams !== undefined) key.push(searchParams);
  return key;
}

function deriveModelKey(url: string): string[] {
  const segments = url.replace(/^\//, '').split('/').filter(Boolean);
  if (segments.length >= 2 && segments[0] === 'admin') {
    return [segments[0], segments[1]];
  }
  return segments.length > 0 ? [segments[0]] : [];
}

function extractErrorMessage(error: any): string {
  const axiosMessage =
    error?.response?.data?.message || error?.response?.data?.error || error?.response?.statusText;
  if (axiosMessage) return String(axiosMessage).substring(0, 300);
  if (error?.message) return error.message.substring(0, 300);
  return 'Request failed';
}

export function useFetchQuery<T>(
  options: Options,
  queryOptions?: Omit<UseQueryOptions<T>, 'queryFn' | 'queryKey'> & {
    messageFail?: null | string;
  },
): UseQueryResult<T> {
  const auth = useAuth0();
  const { token: localToken } = useLocalAuth();
  const router = useRouter();
  const { messageFail, ...restQueryOptions } = queryOptions || {};

  const result = useQuery<T>({
    queryFn: ({ signal }) =>
      fetchAPI<T>(
        {
          ...options,
          headers: { 'x-api-variant': router.pathname, ...options.headers },
          signal,
          url: `${PUBLIC_API_URL}${API_PREFIX}${options.url}`,
        },
        { auth, throwOnError: true, useLocalToken: !!localToken },
      ),
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: deriveKey(options.url, options.params, options.searchParams),
    ...restQueryOptions,
  });

  useEffect(() => {
    if (result.error && messageFail !== null) {
      enqueueSnackbar(messageFail || extractErrorMessage(result.error), {
        autoHideDuration: 3000,
        SnackbarProps: { 'data-testid': 'snackbar-error' } as SnackbarPropsExtended,
        variant: 'error',
      });
    }
  }, [result.error, messageFail]);

  return result;
}

export function useFetchMutation<T, V = any>(
  options: Omit<Options, 'data'>,
  mutationOptions?: Omit<UseMutationOptions<T, Error, V>, 'mutationFn' | 'mutationKey'> & {
    messageFail?: null | string;
    messageSuccess?: null | string;
  },
): UseMutationResult<T, Error, V> {
  const auth = useAuth0();
  const { token: localToken } = useLocalAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const method = options.method || 'POST';

  const {
    messageFail,
    messageSuccess,
    onError: callerOnError,
    onSuccess: callerOnSuccess,
    ...restMutationOptions
  } = mutationOptions || {};

  return useMutation<T, Error, V>({
    ...restMutationOptions,
    mutationFn: (data: V) =>
      fetchAPI<T>(
        {
          ...options,
          data,
          headers: { 'x-api-variant': router.pathname, ...options.headers },
          method,
          url: `${PUBLIC_API_URL}${API_PREFIX}${options.url}`,
        },
        { auth, throwOnError: true, useLocalToken: !!localToken },
      ),
    mutationKey: deriveKey(options.url),
    onError: (error, variables, context) => {
      if (messageFail !== null) {
        enqueueSnackbar(messageFail || extractErrorMessage(error), {
          autoHideDuration: 3000,
          SnackbarProps: { 'data-testid': 'snackbar-error' } as SnackbarPropsExtended,
          variant: 'error',
        });
      }
      callerOnError?.(error, variables, context);
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: deriveModelKey(options.url) });
      if (messageSuccess) {
        enqueueSnackbar(messageSuccess, {
          autoHideDuration: 3000,
          SnackbarProps: { 'data-testid': 'snackbar-success' } as SnackbarPropsExtended,
          variant: 'success',
        });
      }
      await callerOnSuccess?.(data, variables, context);
    },
  });
}
